import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { get, isEmpty, isNil } from 'lodash';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Grid, Typography, IconButton, Alert, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import UILink from '@mui/material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';

import {
  dashboardPage, localeDateTimeFormat, toLocaleStringDateFormat,
} from '../../lib/utils';
import marketsActions from '../../actions/markets';
import WrappedSpinner from '../common/wrapped-spinner';
import DashboardCard from './dashboard-card';

dayjs.extend(timezone);
dayjs.extend(utc);

const styles = {
  container: {
    maxWidth: '1600px',
  },
  check: {
    transform: 'scale(2.5)',
  },
  block: {
    minHeight: '100%',
    p: 1.5,
    borderRadius: 2,
    bgcolor: 'background.paper',
  },
};

function DashboardPage({ match, dashboardData, loading, selectedMarket, fetchDashboardData }) {
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  const [outageDate, setOutageDate] = useState('');
  const tz = get(selectedMarket, 'tz', 'America/New_York');
  const storedOutageDate = localStorage.getItem('outageDate');
  const theme = useTheme();

  const fetchData = () => {
    const marketId = get(match, 'params.marketId');

    if (marketId) {
      fetchDashboardData({ market_id: marketId });
      setLastUpdateTime(new Date());
    }
  };

  const handleNetworkOutage = () => {
    if (!isEmpty(dashboardData) && !isEmpty(dashboardData.outageData)) {
      if (isNil(storedOutageDate)) {
        const alertDate = new Date();
        setOutageDate(alertDate);
        localStorage.setItem('outageDate', alertDate);
      } else setOutageDate(storedOutageDate);
    } else if (!isEmpty(dashboardData) && !isNil(storedOutageDate)) localStorage.removeItem('outageDate');
  };

  const getAlerts = () => (
    <Grid sx={{ mb: 3 }}>
      {dashboardData.outageData && dashboardData.outageData.map(
        (networkOutage) => {
          const cellNetwork = Object.keys(networkOutage)[0];
          const outagePercentage = Object.values(networkOutage)[0];
          const lastUpdated = dayjs().tz(tz).diff(dayjs(storedOutageDate).tz(tz), 'h');
          const hourText = lastUpdated > 1 ? 'hours' : 'hour';
          const lastUpdateText = ` ${lastUpdated} ${hourText} ago `;
          const outageAlterText = `Possible network outage detected ${lastUpdated > 0 ? lastUpdateText : ''}(${dayjs(outageDate).tz(tz).format(localeDateTimeFormat)}): ${outagePercentage}% of In Field and Warehouse vehicles in the ${cellNetwork} network have recently gone offline. Learn more on what you need to do `;
          return (
            <Alert key={cellNetwork} variant="filled" severity="warning" sx={{ mb: 1 }}>
              {outageAlterText}
              <Tooltip title="Confluence Doc">
                <UILink
                  href={dashboardData.networkOutageConflunceDocURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  color={theme.palette.primary.contrastText}
                >
                  here.
                </UILink>
              </Tooltip>
            </Alert>
          );
        },
      )}
    </Grid>
  );

  useEffect(() => {
    fetchData();
  }, [match]);

  useEffect(() => {
    if (dashboardData && !isEmpty(dashboardData)) {
      handleNetworkOutage();
    }
  }, [dashboardData]);

  const alerts = Object.keys(dashboardData || {})
    .filter((key) => ['red', 'lightred'].includes(dashboardData[key].color))
    .sort((key1, key2) => {
      const i1 = dashboardPage.sortOrder.indexOf(key1);
      const i2 = dashboardPage.sortOrder.indexOf(key2);
      return i1 < i2 ? -1 : 1;
    })
    .sort((key1, key2) => {
      if (dashboardData[key1].color === 'red') return -1;
      if (dashboardData[key2].color === 'red') return 1;
      return 0;
    })
    .slice(0, 3);
  const count = alerts.length;

  return (
    <Grid container justifyContent="center" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: 1 }}>
      <WrappedSpinner isLoading={!dashboardData || loading}>
        {dashboardData && (
          <Grid container justifyContent="center" spacing={2} style={styles.container}>
            <Grid item xs={12} lg={10.5}>
              {getAlerts()}
              <Grid sx={{ ...styles.block }}>
                <Typography variant="h5">
                  {`You have ${count || 'no'} alert${count === 1 ? '' : 's'}`}
                </Typography>
                <Typography variant="caption">
                  {`Last Updated ${dayjs(lastUpdateTime).tz(tz).format(toLocaleStringDateFormat)}`}
                </Typography>
                <IconButton
                  size="small"
                  sx={{ ml: 0.5 }}
                  onClick={fetchData}
                >
                  <RefreshIcon />
                </IconButton>
                {count ? (
                  <Grid container justifyContent="center" spacing={2} sx={{ pt: 1 }}>
                    {alerts.map((key) => (
                      <DashboardCard
                        key={key}
                        name={key}
                        data={dashboardData[key]}
                        size="large"
                        marketId={match.params.marketId}
                      />
                    ))}
                  </Grid>
                ) : (
                  <Grid container direction="column" alignItems="center" sx={{ pt: 1, pb: 2 }}>
                    <CheckIcon fontSize="large" style={styles.check} sx={{ color: 'grey.600', mb: 3 }} />
                    <Typography variant="caption" sx={{ color: 'grey.600' }}>
                      Well done! No active alerts.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} sm={4} lg={3.5}>
              <Grid sx={{ ...styles.block }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Fleet health
                </Typography>
                <Grid container spacing={1}>
                  {dashboardPage.structure.fleet.map((key) => (
                    <DashboardCard
                      key={key}
                      name={key}
                      data={dashboardData[key]}
                      marketId={match.params.marketId}
                    />
                  ))}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={4} lg={3.5}>
              <Grid sx={{ ...styles.block }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Scooters at risk
                </Typography>
                <Grid container spacing={1}>
                  {dashboardPage.structure.scooters.map((key) => (
                    <DashboardCard
                      key={key}
                      name={key}
                      data={dashboardData[key]}
                      marketId={match.params.marketId}
                    />
                  ))}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={4} lg={3.5}>
              <Grid sx={{ ...styles.block }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Tasks
                </Typography>
                <Grid container spacing={1}>
                  {dashboardPage.structure.tasks.map((key) => (
                    <DashboardCard
                      key={key}
                      name={key}
                      data={dashboardData[key]}
                      marketId={match.params.marketId}
                    />
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </WrappedSpinner>
    </Grid>
  );
}

function mapStateToProps({ markets }) {
  return {
    dashboardData: markets.dashboardData,
    loading: markets.isLoading,
    selectedMarket: markets.selectedMarket,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    { ...marketsActions },
    dispatch,
  );
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DashboardPage));
